import React, { FC, useMemo, useState } from 'react';
import { AthleteAttendanceTeam } from '../../../../../models/attendance.models';
import { PeriodBreakdownItem, WeekBreakdownItem } from '../../../../../models/stats.models';
import PeriodDetailBreakdown from '../../../../../components/period-summary/period-detail-breakdown.component';
import {
  buildBarSegments,
  formatPeriodDateRangeLabel,
  getPeriodStatus,
  getUpcomingPeriodStartLabel,
} from '../../../../../components/period-summary/period-summary.helpers';
import { getElevationMeasurementUnit, getMeasurementUnit, ZONE_CONFIGS } from '../../../../../helpers/general.helpers';
import { formatEffortShort, formatSeasonHoursHeader } from '../log.helpers';

interface LogPeriodSummaryProps {
  period: PeriodBreakdownItem;
  weeks: WeekBreakdownItem[];
  attendanceTeams?: AthleteAttendanceTeam[] | null;
  attendanceLoading?: boolean;
}

const LogPeriodSummary: FC<LogPeriodSummaryProps> = ({ period, weeks, attendanceTeams, attendanceLoading }) => {
  const [expanded, setExpanded] = useState(false);
  const distanceUnit = getMeasurementUnit();
  const elevationUnit = getElevationMeasurementUnit();
  const status = useMemo(() => getPeriodStatus(period, weeks), [period, weeks]);
  const isUpcoming = status === 'upcoming' && (period.duration?.total ?? 0) === 0;

  const zoneBarSegments = useMemo(
    () =>
      buildBarSegments(
        period.totalDurationBreakdown ?? [],
        ZONE_CONFIGS.map((z) => z.barColor)
      ),
    [period.totalDurationBreakdown]
  );

  if (isUpcoming) {
    return (
      <div className="log-period-summary">
        <div className="log-period-empty">
          <div className="log-period-empty-title">No training data yet</div>
          <div className="log-period-empty-desc">
            This period starts {getUpcomingPeriodStartLabel(period)}. Data will appear here as workouts are logged.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="log-period-summary">
      <div className="log-period-summary-card">
        <div
          className="log-period-kpi-bar"
          onClick={() => setExpanded((v) => !v)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setExpanded((v) => !v)}
        >
          <div className="log-period-kpi-left">
            <div className="log-kpi-block log-kpi-period">
              <div className="log-kpi-val-lg">Period {period.index}</div>
              <div className="log-kpi-lbl-sm">{formatPeriodDateRangeLabel(period.period)}</div>
            </div>
            <div className="log-kpi-divider" />
            <div className="log-kpi-block">
              <div className="log-kpi-val">{formatSeasonHoursHeader(period.duration?.total ?? 0)}</div>
              <div className="log-kpi-lbl-sm">Duration</div>
            </div>
            <div className="log-kpi-divider" />
            <div className="log-kpi-block">
              <div className="log-kpi-val">
                {Math.round(period.distance?.total ?? 0).toLocaleString()}
                <span className="log-kpi-unit"> {distanceUnit}</span>
              </div>
              <div className="log-kpi-lbl-sm">Distance</div>
            </div>
            <div className="log-kpi-divider" />
            <div className="log-kpi-block">
              <div className="log-kpi-val">
                {(period.elevationGain?.total ?? 0).toLocaleString()}
                <span className="log-kpi-unit"> {elevationUnit}</span>
              </div>
              <div className="log-kpi-lbl-sm">Ascent</div>
            </div>
            <div className="log-kpi-divider" />
            <div className="log-kpi-block">
              <div className="log-kpi-val">{formatSeasonHoursHeader(period.duration?.weeklyAverage ?? 0)}</div>
              <div className="log-kpi-lbl-sm">Avg/Wk</div>
            </div>
            <div className="log-kpi-divider" />
            <div className="log-kpi-block">
              <div className="log-kpi-val">{formatEffortShort(period.effortRatings?.average)}</div>
              <div className="log-kpi-lbl-sm">Avg Effort</div>
            </div>
          </div>
          <div className="log-period-kpi-right">
            <div className="log-period-zone-bar">
              {zoneBarSegments.map((segment, index) => (
                <span key={index} style={{ background: segment.color, flex: segment.flex }} />
              ))}
            </div>
            <span className="log-period-chev">{expanded ? '▴' : '▾'}</span>
          </div>
        </div>

        {expanded && (
          <div className="log-period-detail">
            <PeriodDetailBreakdown
              period={period}
              attendanceTeams={attendanceTeams}
              attendanceLoading={attendanceLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LogPeriodSummary;
